/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import {useTranslation} from "react-i18next";
import React from "react";
import {CustomLevelCardStyle} from "../../../../../../components/Styles";
import {
    IconArrowRightFromLine,
    IconExitDevice,
    IconPackageReturn,
    IconSwapHorizontal,
    IconVehicleArrowArrive,
    IconVehicleArrowDeparture,
    IconVehicleArrowToRight
} from "../../../../../../components/buttons/IconButtons";
import {
    ENTRY_AND_EXIT_FORMS,
    getGateLogEmptyText,
    getMaterialRequestTableStatusId,
    getMovementDirectionOption,
    getMovementDirectionOptionByValue,
    MATERIAL_CONTROL_GATE_EVENT_TYPE,
    MOVEMENT_DIRECTION
} from "../../../Model/Model";
import {EMPTY_VALUES, getDateFormat, sanitizeString} from "../../../../../../Apis/functions";
import {FILE_MODEL} from "../../../../Visitor/Visitor/Model/Model";
import {STATUS_MODEL} from "../../../../../../enums/statusEnum";
import DetailsCardHeader from "../GeneralEntryExit/Details/Sections/DetailsCardHeader";
import DetailsSectionRow from "../GeneralEntryExit/Details/Sections/DetailsSectionRow";
import ContactSection from "../GeneralEntryExit/Details/Sections/ContactSection";
import DepartmentsSection from "../GeneralEntryExit/Details/Sections/DepartmentsSection";
import LabeledTextSection from "../GeneralEntryExit/Details/Sections/LabeledTextSection";
import MovementTypeSection from "../GeneralEntryExit/Details/Sections/MovementTypeSection";
import VehicleArrivalSection from "../GeneralEntryExit/Details/Sections/VehicleArrivalSection";
import MovementRecordSection from "../GeneralEntryExit/Details/Sections/MovementRecordSection";
import EvidenceSection from "../GeneralEntryExit/Details/Sections/EvidenceSection";

/**
 * Sections shared by every form type, so a form only declares what it captures.
 *
 * - Icon: identifies the block that holds the information of the form.
 * - movementDirection: direction fixed by the form, or null when the folio is
 *   the one that carries it.
 * - showMovementType: the forms that do not choose a direction hide the block.
 * - movementTypeBeforeFields: the movements that declare no value of their own
 *   read the block of the movement right after the areas that follow the folio,
 *   because what the vehicle does is what the detail opens with.
 * - fields: rows of the form captured between the departments and the movement
 *   type. A row reads the folio itself, or the block named by source, is
 *   formatted as a date when isDate is declared, and is named after the
 *   direction of the movement when titleByDirection is declared, because the
 *   forms that let the user choose it read the same value as the entry or the
 *   exit of the material. A row marked with gateCompleted is optional for the
 *   requester and is written by the guard at the gate, so the one left blank is
 *   read as waiting for it instead of as a value the folio does not carry.
 * - showEvidence: the evidence attached when the form was registered.
 * - showNotes: the notes the requester left, hidden by the movements that
 *   already read them as a row of their own.
 * - showTripExit: the exit of the trip registered by the guard at the gate.
 * - tripByMovementDirection: the record of the trip is titled by the direction
 *   the folio takes, because the same movement is read as the entry of the
 *   materials when they come into the plant and as their exit when they leave.
 * - arrivalRequirementStatuses: statuses the requirement of the return is read
 *   in, because it is only asked for while the folio waits for the vehicle.
 * - arrivalTrans / arrivalDescription: labels the requirement and the record are
 *   read with, so a movement that comes into the plant is closed with the
 *   departure of the vehicle instead of its arrival.
 * - showArrivalRecord: the return registered by the guard, hidden by the
 *   movements that are only closed with the exit of the trip.
 * - arrivalRecordGateType / tripExitGateType: movements the gate registers each
 *   record with, because both are read from the gate events of the folio.
 */
const DETAILS_SECTIONS_DEFAULTS = {
    Icon: IconExitDevice,
    movementDirection: null,
    showMovementType: true,
    movementTypeBeforeFields: false,
    fields: [],
    showEvidence: false,
    showNotes: true,
    showTripExit: false,
    tripByMovementDirection: false,
    arrivalRequirementStatuses: [STATUS_MODEL.PENDING_ARRIVAL],
    arrivalTrans: 'vehicle_arrival',
    arrivalRecordTrans: 'vehicle_arrival',
    tripExitTrans: 'trip_exit',
    movementTypeTrans: 'movement_type',
    headerIcon: null,
    arrivalRecordIcon: IconVehicleArrowArrive,
    arrivalRecordIconBackground: 'background-primary-100',
    tripExitIcon: IconArrowRightFromLine,
    tripExitIconBackground: 'background-warning-100',
    arrivalDescription: 'the_folio_will_stay_open_until_the_arrival_is_registered',
    showArrivalRecord: true,
    showDepartureRecord: true,
    departureRecordTrans: 'vehicle_departure',
    departureRecordIcon: IconVehicleArrowDeparture,
    departureRecordIconBackground: 'background-primary-100',
    arrivalRecordGateType: MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.ARRIVAL_RECORDED],
    departureRecordGateType: MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.DEPARTURE_RECORDED],
    tripExitGateType: MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.DISPATCHED]
};

const DETAILS_SECTIONS_BY_FORM = {
    [ENTRY_AND_EXIT_FORMS.GENERAL_ENTRY_EXIT.key]: {
        Icon: IconExitDevice,
        // The vehicle travels with the folio as its own block, so the detail
        // reads it the same way the requester registered it
        fields: [
            // The vehicle and the date of the movement are the ones the
            // requester registers the folio with, and the gate has nowhere to
            // complete them, so what it was registered without is read as a
            // value the folio does not carry
            {title: 'vehicle_information', name: 'vehicleInformation', source: 'transport'},
            {title: 'expected_exit_date', titleByDirection: true, name: 'expectedMovementDate', isDate: true}
        ],
        showEvidence: true
    },
    [ENTRY_AND_EXIT_FORMS.PLANT_MATERIAL_CONTROL.key]: {
        Icon: IconSwapHorizontal,
        // The vehicle of the movement and the date it happens are read at the
        // gate, so the folio declares no values of its own and the movement the
        // gate registered is the one read below the request
        showTripExit: true,
        tripByMovementDirection: true
    },
    [ENTRY_AND_EXIT_FORMS.CRATES_AND_PALLETS_RETURN.key]: {
        Icon: IconPackageReturn,
        // The direction is fixed by the form and is not captured when the folio
        // is registered, so it is only used to title the movement of the gate
        // and the block that names it is not read
        movementDirection: MOVEMENT_DIRECTION.EXIT,
        showMovementType: false,
        showTripExit: true
    },
    [ENTRY_AND_EXIT_FORMS.POULTRY_TRUCK_EXIT.key]: {
        Icon: IconVehicleArrowToRight,
        showMovementType: false,
        // The truck is expected on the date the folio declares, so the date is
        // asked for when it is registered and the gate does not complete it
        fields: [{title: 'expected_exit_date', name: 'expectedMovementDate', isDate: true}],
        showTripExit: true
    },
    [ENTRY_AND_EXIT_FORMS.LIVE_POULTRY_TRUCK.key]: {
        Icon: IconVehicleArrowToRight,
        fields: [
            {
                title: 'expected_exit_date',
                titleByDirection: true,
                name: 'expectedMovementDate',
                isDate: true,
                gateCompleted: true
            }
        ],
        // The truck comes into the plant or leaves it, so the movement the gate
        // registered is titled by the direction of the folio, the same way the
        // plant material control reads it
        showTripExit: true,
        tripByMovementDirection: true
    }
};

/**
 * Returns the sections declared by the given form type.
 *
 * The forms registered before the type was captured are read as a general entry
 * and exit, which is the movement every plant registers.
 *
 * @param {string} formKey - Key of the form type.
 *
 * @returns {Object} Sections the detail of that form displays.
 */
const getDetailsSections = (formKey) => ({
    ...DETAILS_SECTIONS_DEFAULTS,
    ...(DETAILS_SECTIONS_BY_FORM[formKey] ||
        DETAILS_SECTIONS_BY_FORM[ENTRY_AND_EXIT_FORMS.GENERAL_ENTRY_EXIT.key])
});

/**
 * Reads the value a row of the form displays.
 *
 * A row reads the folio itself, or the block of it named by source, because the
 * service keeps the vehicle and the materials as blocks of their own.
 *
 * @param {Object} item - Folio being displayed.
 * @param {Object} field - Row declared by the form.
 *
 * @returns {string|number|null} Value the row is read with.
 */
const getFieldValue = (item, field) => {
    const value = field.source ? item?.[field.source]?.[field.name] : item?.[field.name];

    return field.isDate && value ? getDateFormat(value) : value;
};

/**
 * Reads the movement the folio declared for the gate.
 *
 * The requirement is captured when the form is registered and says whether the
 * guard still has to write that movement, so the folio that travels one way
 * carries it as well and is read saying that it is not waited for.
 *
 * @param {Object} item - Folio being displayed.
 * @param {string} type - Movement the requirement belongs to.
 *
 * @returns {Object|null} Requirement of that movement, or null when the folio
 * does not declare it.
 */
const getGateRequirement = (item, type) => (item?.gateRequirements || [])
    .find((e) => e.gateEventType === type) || null;

/**
 * Reads the movement the gate registered of the given type.
 *
 * The movements are returned in the order they happened, so the last one is the
 * one the record of the folio is read with.
 *
 * @param {Object} item - Folio being displayed.
 * @param {string} type - Movement the record belongs to.
 *
 * @returns {Object|null} Movement the gate registered, or null when it has none.
 */
const getGateEvent = (item, type) => (item?.gateEvents || [])
    .filter((e) => e.type === type)
    .slice(-1)[0] || null;

/**
 * Details the requester left for a movement of the gate.
 *
 * The folio asks for either the vehicle that moves with no material or the
 * details of what it carries, so the movement marked as empty is read with the
 * label that says so and the rest with what the requester wrote for it.
 *
 * @param {Function} t - Translation handler.
 * @param {Object} requirement - Requirement of the movement.
 * @param {string} emptyTrans - Label the movement with no material is read with.
 *
 * @returns {JSX.Element|null} Notes of the movement, or nothing when it carries none.
 */
const GateRequirementNotes = ({t, requirement, emptyTrans}) => {
    // A movement the folio does not wait for carries no details, so nothing is
    // read below it even when the requirement travels with the ones it had
    const value = requirement?.required === false
        ? null
        : (requirement?.arriveEmpty
            ? t(emptyTrans)
            : sanitizeString(requirement?.notes));

    if (!value)
        return null;

    return (
        <>
            <div className={'border-bottom-shades-300 my-3'}/>

            <LabeledTextSection
                t={t}
                title={'notes'}
                value={value}
            />
        </>
    );
};

/**
 * Information card of an entry and exit form.
 *
 * Every form type reuses the same rows, so the card is built once and the type
 * of the form only decides which sections it displays.
 *
 * @returns {JSX.Element} Card with the information of the folio.
 */
const EntryAndExitFormDetailsSectionInfo = ({
                                                item,
                                                evidence,
                                                form,
                                                contentCardRef,
                                                sections: sectionsOverride,
                                                statusId: statusIdOverride,
                                                movementType: movementTypeOverride,
                                                showDepartureRecord = true,
                                                showTripRecord = true,
                                                // A detail that already reads the notes of the folio in a
                                                // section of its own does not repeat them in the card
                                                showNotes: showNotesProp = true
                                            }) => {

    const {t} = useTranslation();
    // The movements that are not registered with a form declare their own
    // sections and carry the status the gate reads them by
    const statusId = statusIdOverride ?? getMaterialRequestTableStatusId(item);
    // A value the gate completes is waited for while the folio is open, and is
    // read as a value the folio does not carry once the movement was dispatched
    // and nobody writes on it any more
    const gateEmptyText = getGateLogEmptyText(item, t);
    // The status is named by the chip of the header, so the card is read with
    // the same borders whatever the folio is left in
    const styles = CustomLevelCardStyle(null, false);
    const sections = sectionsOverride
        ? {...DETAILS_SECTIONS_DEFAULTS, ...sectionsOverride}
        : getDetailsSections(form?.key);
    // The direction fixed by the form is the one it declares, while the one of
    // the folio is read with the text the service stores it as
    const movementType = movementTypeOverride
        || getMovementDirectionOption(sections.movementDirection)
        || getMovementDirectionOptionByValue(item?.movementDirection);

    // The movement is read right after the areas that follow the folio when the
    // folio has no values of its own to describe before it, so the block that
    // names the movement travels to the place its detail declares
    const movementTypeBlock = sections.showMovementType && (
        <>
            <div className={'border-bottom-shades-300 my-3'}/>

            <MovementTypeSection
                t={t}
                movementType={movementType}
                title={sections.movementTypeTrans}
            />
        </>
    );

    // The people and the areas the folio is followed by travel as blocks of
    // their own, so the rows read every one of them; a folio registered without
    // watchers is followed by the person that requested it
    const watchers = item?.watchers?.length > 0
        ? item.watchers.map((e) => ({name: e.user, email: e.userEmail, phoneNumber: e.userPhoneNumber}))
        : [{
            name: item?.requestedBy,
            email: item?.requestedByEmail,
            phoneNumber: item?.requestedByPhoneNumber
        }];
    const departments = (item?.departments || []).map((e) => e.department);
    // Every record of a movement is read with the event the gate registered it
    // with: who wrote it, when it happened and the photos taken at that moment
    const arrivalRecord = getGateEvent(item, sections.arrivalRecordGateType);
    const departureRecord = getGateEvent(item, sections.departureRecordGateType);
    const tripExitRecord = getGateEvent(item, sections.tripExitGateType);
    // The departure of the vehicle is asked for by the folio itself and not by
    // the status it waits in, so the requirement is the one that says whether
    // the movement is read, together with the details the requester left for it
    const departureRequirement = getGateRequirement(
        item,
        MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.DEPARTURE_RECORDED]
    );
    // The arrival of the vehicle carries the same requirement, so the details
    // the requester left for it are read from it as well
    const arrivalRequirement = getGateRequirement(
        item,
        MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.ARRIVAL_RECORDED]
    );

    // The requirement of the arrival is shown while the folio still waits for the
    // vehicle, and both movements of the gate are read once it registered them.
    // The requirement is asked for by the folio itself, the same way the
    // departure is, so a movement that leaves the plant keeps reading the
    // arrival it declared once the vehicle is no longer awaited; the status is
    // still read for the folios that are returned with no requirements.
    const showVehicleArrivalRequirement = arrivalRequirement !== null
        || sections.arrivalRequirementStatuses.includes(statusId);
    const showVehicleArrivalRecord = sections.showArrivalRecord && arrivalRecord !== null;
    // The departure the gate registered is read the same way the arrival is, so
    // the folio that left the plant displays the log the guard wrote for it.
    // The gate writes that log on its own screen, so the detail the guard reads
    // does not repeat it.
    const showVehicleDepartureRecord = showDepartureRecord
        && sections.showDepartureRecord
        && departureRecord !== null;
    // The gate writes the movement of the materials on its own screen, so the
    // detail the guard reads does not repeat it, the same way it does not
    // repeat the departure it registered
    const showTripExit = showTripRecord && sections.showTripExit && tripExitRecord !== null;
    // The movement the gate registered is the entry of the materials when they
    // come into the plant and their exit when they leave it, so the record is
    // read with the direction the folio takes
    const isTripEntry = sections.tripByMovementDirection
        && movementType?.key === MOVEMENT_DIRECTION.ENTRY;
    const tripTrans = isTripEntry ? 'trip_entry' : sections.tripExitTrans;
    const TripIcon = isTripEntry ? movementType.Icon : sections.tripExitIcon;
    const tripIconBackground = isTripEntry
        ? movementType.iconBackground
        : sections.tripExitIconBackground;
    // The evidence of the folio only takes the room left on the screen while
    // nothing else is registered below it, because the movements of the gate
    // are read under it and a list that fills the screen hides them
    const hasGateRecords = showVehicleArrivalRecord || showVehicleDepartureRecord || showTripExit;
    // The notes are read on their own, and only by the folios that carry them
    const showNotes = showNotesProp && sections.showNotes && Boolean(item?.extraDetails);

    return (
        <div style={styles.container}>
            <DetailsCardHeader
                t={t}
                statusId={statusId}
                style={styles.levelCard}
                {...(sections.headerIcon ? {Icon: sections.headerIcon} : {})}
            />

            <div className='row align-items-start p-3 overflow-y-auto' ref={contentCardRef}>
                <DetailsSectionRow
                    Icon={sections.Icon}
                    iconBackgroundClass={'background-secondary-100'}
                >
                    <div className='row px-0 ml-0 pl-0'>
                        <div className='col-12 px-0'>
                            <p className='font-color-general-950 fs-14px ibl-font-w-600 px-0 text-uppercase'>
                                {item?.code}
                            </p>
                            <p className='font-color-general-950 fs-14px ibl-font-w-400 px-0 mb-1'>
                                {item?.title || EMPTY_VALUES.DEFAULT}
                            </p>
                        </div>

                        <div className={'border-bottom-shades-300 my-3'}/>

                        <ContactSection
                            t={t}
                            title={'requested_by'}
                            name={item?.requestedBy}
                            email={item?.requestedByEmail}
                            phoneNumber={item?.requestedByPhoneNumber}
                        />

                        <div className={'border-bottom-shades-300 my-3'}/>

                        <ContactSection
                            t={t}
                            title={'watchers'}
                            contacts={watchers}
                        />

                        <div className={'border-bottom-shades-300 my-3'}/>

                        <DepartmentsSection
                            t={t}
                            departments={departments}
                        />

                        {/* A movement read before the values of the folio names
                        what the vehicle does, and the values that follow it
                        describe the movement it takes. */}
                        {sections.movementTypeBeforeFields && movementTypeBlock}

                        {/* The values the form declares are read right after the
                        areas that follow the folio, because they describe what
                        was registered before the movement it takes. */}
                        {sections.fields.map((field) =>
                            <React.Fragment key={field.name}>
                                <div className={'border-bottom-shades-300 my-3'}/>

                                <LabeledTextSection
                                    t={t}
                                    title={field.titleByDirection
                                        ? (movementType?.dateTrans || field.title)
                                        : field.title}
                                    value={getFieldValue(item, field)}
                                    {...(field.gateCompleted ? {emptyText: gateEmptyText} : {})}
                                />
                            </React.Fragment>
                        )}

                        {!sections.movementTypeBeforeFields && movementTypeBlock}

                        {showVehicleArrivalRequirement &&
                            <>
                                <div className={'border-bottom-shades-300 my-3'}/>

                                <VehicleArrivalSection
                                    t={t}
                                    title={sections.arrivalTrans}
                                    description={sections.arrivalDescription}
                                    required={arrivalRequirement?.required !== false}
                                />

                                <GateRequirementNotes
                                    t={t}
                                    requirement={arrivalRequirement}
                                    emptyTrans={'vehicle_will_arrive_empty'}
                                />
                            </>
                        }

                        {departureRequirement &&
                            <>
                                <div className={'border-bottom-shades-300 my-3'}/>

                                <VehicleArrivalSection
                                    t={t}
                                    id={'vehicle_departure'}
                                    title={'vehicle_departure'}
                                    description={'the_folio_will_stay_open_until_the_departure_is_registered'}
                                    required={departureRequirement.required !== false}
                                />

                                <GateRequirementNotes
                                    t={t}
                                    requirement={departureRequirement}
                                    emptyTrans={'vehicle_will_leave_empty'}
                                />
                            </>
                        }

                        {showNotes &&
                            <>
                                <div className={'border-bottom-shades-300 my-3'}/>

                                <LabeledTextSection
                                    t={t}
                                    title={'notes'}
                                    value={item?.extraDetails}
                                />
                            </>
                        }

                        {sections.showEvidence &&
                            <>
                                <div className={'border-bottom-shades-300 mt-3'}/>

                                <EvidenceSection
                                    t={t}
                                    item={item}
                                    evidence={evidence}
                                    fillHeight={!hasGateRecords}
                                />
                            </>
                        }
                    </div>
                </DetailsSectionRow>

                {showVehicleArrivalRecord &&
                    <DetailsSectionRow
                        Icon={sections.arrivalRecordIcon}
                        iconBackgroundClass={sections.arrivalRecordIconBackground}
                        className={'mt-4'}
                    >
                        <MovementRecordSection
                            t={t}
                            title={sections.arrivalRecordTrans}
                            item={item}
                            evidence={(arrivalRecord?.evidence || []).map((e) => FILE_MODEL.set(e))}
                            registeredBy={arrivalRecord?.registeredBy}
                            date={arrivalRecord?.eventDate}
                            notes={arrivalRecord?.notes}
                            fillHeight={!showVehicleDepartureRecord && !showTripExit}
                        />
                    </DetailsSectionRow>
                }

                {/* The records are read in the order the gate wrote them, so the
                movement of the materials is read before the vehicle that takes
                them out of the plant. */}
                {showTripExit &&
                    <DetailsSectionRow
                        Icon={TripIcon}
                        iconBackgroundClass={tripIconBackground}
                        className={'mt-4'}
                    >
                        <MovementRecordSection
                            t={t}
                            title={tripTrans}
                            item={item}
                            evidence={(tripExitRecord?.evidence || []).map((e) => FILE_MODEL.set(e))}
                            registeredBy={tripExitRecord?.registeredBy}
                            date={tripExitRecord?.eventDate}
                            notes={tripExitRecord?.notes}
                            fillHeight={!showVehicleDepartureRecord}
                            evidenceTrans={'evidence'}
                        />
                    </DetailsSectionRow>
                }

                {showVehicleDepartureRecord &&
                    <DetailsSectionRow
                        Icon={sections.departureRecordIcon}
                        iconBackgroundClass={sections.departureRecordIconBackground}
                        className={'mt-4'}
                    >
                        <MovementRecordSection
                            t={t}
                            title={sections.departureRecordTrans}
                            item={item}
                            evidence={(departureRecord?.evidence || []).map((e) => FILE_MODEL.set(e))}
                            registeredBy={departureRecord?.registeredBy}
                            date={departureRecord?.eventDate}
                            notes={departureRecord?.notes}
                            fillHeight={true}
                            evidenceTrans={'evidence'}
                        />
                    </DetailsSectionRow>
                }
            </div>
        </div>
    );
};

export default EntryAndExitFormDetailsSectionInfo;
